import type { MatchmakingSuggestions } from './types'

export interface TopSuggestion {
  id: string
  name: string
  score: number
  tags: MatchmakingSuggestions['groups']['people'][number]['matched_tags']
}

/**
 * Recorte curto para a home: as melhores sugestões entre **todos** os grupos.
 *
 * Feito no cliente sobre a resposta padrão de propósito. Pedir `per_group: 3` ao
 * servidor devolveria as três melhores de cada tipo — doze no total — e não as
 * três melhores no geral, que é o que o bloco promete.
 *
 * Empate de score preserva a ordem que o servidor devolveu, que já é
 * determinística por tipo. `sort` do JS é estável desde ES2019.
 */
export const selectTopSuggestions = (
  groups: MatchmakingSuggestions['groups'] | undefined,
  limit: number
): TopSuggestion[] => {
  if (!groups || limit <= 0) return []

  const flattened: TopSuggestion[] = [
    ...groups.people.map(s => ({
      id: `person-${s.record.id}`,
      name: s.record.name ?? 'Participante',
      score: s.score,
      tags: s.matched_tags
    })),
    ...groups.partners.map(s => ({
      id: `partner-${s.record.id}`,
      name: s.record.name,
      score: s.score,
      tags: s.matched_tags
    })),
    ...groups.programmings.map(s => ({
      id: `programming-${s.record.id}`,
      name: s.record.name,
      score: s.score,
      tags: s.matched_tags
    })),
    ...groups.journeys.map(s => ({
      id: `journey-${s.record.id}`,
      name: s.record.name,
      score: s.score,
      tags: s.matched_tags
    }))
  ]

  return flattened.sort((a, b) => b.score - a.score).slice(0, limit)
}

/** Todos os grupos vazios — o participante tem tags, mas nada cruzou. */
export const hasNoSuggestions = (
  groups: MatchmakingSuggestions['groups'] | undefined
): boolean =>
  !!groups &&
  groups.people.length === 0 &&
  groups.partners.length === 0 &&
  groups.programmings.length === 0 &&
  groups.journeys.length === 0
